import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true,
    },
  ],
  template: `
    <div class="abaq-select-container">
      <div
        class="abaq-select-trigger"
        [class.is-open]="isOpen"
        [class.is-disabled]="disabled"
        [class.is-invalid]="invalid"
      >
        <input
          #inputEl
          type="text"
          [id]="id"
          class="abaq-select-input"
          [value]="displayValue"
          (input)="onSearchInput($event)"
          (focus)="onInputFocus()"
          (blur)="onInputBlur()"
          (keydown)="onInputKeyDown($event)"
          [placeholder]="placeholder"
          [disabled]="disabled"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          [attr.aria-expanded]="isOpen"
        />

        <button
          type="button"
          tabindex="-1"
          class="abaq-select-chevron-btn"
          (click)="toggleOpen($event)"
          [disabled]="disabled"
          aria-label="Abrir opciones"
        >
          <span class="material-symbols-outlined abaq-select-chevron" [class.rotated]="isOpen">
            keyboard_arrow_down
          </span>
        </button>
      </div>

      @if (isOpen) {
        <div class="abaq-select-dropdown" role="listbox">
          @for (opt of filteredOptions; track opt.value; let i = $index) {
            <div
              class="abaq-select-option"
              [class.is-highlighted]="highlightedIndex === i"
              [class.is-selected]="opt.value === value"
              [class.is-disabled]="opt.disabled"
              (mouseenter)="highlightedIndex = i"
              (mousedown)="onSelectOption(opt, $event)"
              role="option"
              [attr.aria-selected]="opt.value === value"
            >
              <span class="option-label">{{ opt.label }}</span>
              @if (opt.value === value) {
                <span class="material-symbols-outlined option-check">check</span>
              }
            </div>
          }
          @if (filteredOptions.length === 0) {
            <div class="abaq-select-no-results">
              Sin coincidencias
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .abaq-select-container {
        position: relative;
        width: 100%;
        display: block;
        box-sizing: border-box;
      }

      .abaq-select-trigger {
        width: 100%;
        min-height: 44px;
        border-radius: 10px;
        border: 1px solid #dce2e8;
        background: #ffffff;
        font-family: inherit;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s ease;
        box-sizing: border-box;
        overflow: hidden;
      }

      .abaq-select-trigger:focus-within,
      .abaq-select-trigger.is-open {
        border-color: #24366f;
        box-shadow: 0 0 0 3px rgba(36, 54, 111, 0.14);
      }

      .abaq-select-trigger.is-invalid {
        border-color: #dc2626 !important;
        background-color: #fff5f5 !important;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.14) !important;
      }

      .abaq-select-trigger.is-disabled {
        background-color: #f1f5f9;
        cursor: not-allowed;
        border-color: #e2e8f0;
      }

      .abaq-select-input {
        flex: 1;
        width: 100%;
        height: 100%;
        min-height: 42px;
        padding: 10px 14px;
        border: none;
        outline: none;
        background: transparent;
        font-family: inherit;
        font-size: 14px;
        color: #1e293b;
        font-weight: 500;
        box-sizing: border-box;
      }

      .abaq-select-input::placeholder {
        color: #94a3b8;
        font-weight: 400;
      }

      .abaq-select-input:disabled {
        color: #94a3b8;
        cursor: not-allowed;
      }

      .abaq-select-chevron-btn {
        background: transparent;
        border: none;
        padding: 0 10px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #64748b;
        outline: none;
      }

      .abaq-select-chevron {
        font-size: 20px;
        transition: transform 0.2s ease;
      }

      .abaq-select-chevron.rotated {
        transform: rotate(180deg);
        color: #24366f;
      }

      .abaq-select-dropdown {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.06);
        max-height: 240px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        z-index: 1050;
        padding: 5px;
        box-sizing: border-box;
        animation: abaqDropdownFade 0.15s ease-out;
      }

      @keyframes abaqDropdownFade {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .abaq-select-option {
        padding: 9px 12px;
        border-radius: 8px;
        font-size: 13.5px;
        color: #1e293b;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        transition: background 0.15s ease;
        user-select: none;
      }

      .abaq-select-option:hover:not(.is-disabled),
      .abaq-select-option.is-highlighted {
        background: rgba(36, 54, 111, 0.06);
        color: #24366f;
      }

      .abaq-select-option.is-selected {
        background: rgba(36, 54, 111, 0.09);
        color: #24366f;
        font-weight: 700;
      }

      .abaq-select-option.is-disabled {
        color: #cbd5e1;
        cursor: not-allowed;
      }

      .option-label {
        flex: 1;
        white-space: normal;
        line-height: 1.35;
        word-break: break-word;
      }

      .option-check {
        font-size: 18px;
        color: #24366f;
      }

      .abaq-select-no-results {
        padding: 12px;
        font-size: 13px;
        color: #94a3b8;
        text-align: center;
      }
    `,
  ],
})
export class CustomSelectComponent implements ControlValueAccessor {
  @Input() id = '';
  @Input() placeholder = 'Selecciona una opción';
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() allowCustomValue = false;

  @Input() set options(val: Array<SelectOption | { value: any; label: string } | string>) {
    this._options = val || [];
  }
  get options(): Array<SelectOption | { value: any; label: string } | string> {
    return this._options;
  }
  private _options: Array<any> = [];

  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('inputEl') inputRef?: ElementRef<HTMLInputElement>;

  isOpen = false;
  value: any = '';
  searchQuery = '';
  isTyping = false;
  highlightedIndex = 0;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  get normalizedOptions(): SelectOption[] {
    return this.options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return opt as SelectOption;
    });
  }

  get filteredOptions(): SelectOption[] {
    if (!this.isTyping || !this.searchQuery.trim()) {
      return this.normalizedOptions;
    }
    const q = this.normalizeStr(this.searchQuery);
    return this.normalizedOptions.filter((opt) =>
      this.normalizeStr(opt.label).includes(q)
    );
  }

  get selectedLabel(): string {
    const found = this.normalizedOptions.find((opt) => opt.value === this.value);
    return found ? found.label : (this.allowCustomValue ? (this.value || '') : '');
  }

  get displayValue(): string {
    if (this.isTyping) {
      return this.searchQuery;
    }
    if (this.selectedLabel) {
      return this.selectedLabel;
    }
    return this.value || '';
  }

  private normalizeStr(str: string): string {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  onInputFocus(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.isTyping = false;
    this.searchQuery = '';
    this.updateHighlightedIndex();
    this.onTouched();
  }

  onInputBlur(): void {
    // Small timeout to allow mousedown on options to execute first
    setTimeout(() => {
      if (this.allowCustomValue && this.isTyping) {
        this.commitSelection(this.searchQuery);
      }
      this.isOpen = false;
      this.isTyping = false;
      this.searchQuery = '';
    }, 180);
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.isTyping = true;
    this.isOpen = true;
    this.highlightedIndex = 0;
    if (this.allowCustomValue) {
      this.value = this.searchQuery;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    }
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen) {
        this.isOpen = true;
        this.highlightedIndex = 0;
      } else {
        const len = this.filteredOptions.length;
        if (len > 0) {
          this.highlightedIndex = (this.highlightedIndex + 1) % len;
        }
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) {
        this.isOpen = true;
        this.highlightedIndex = 0;
      } else {
        const len = this.filteredOptions.length;
        if (len > 0) {
          this.highlightedIndex = (this.highlightedIndex - 1 + len) % len;
        }
      }
    } else if (event.key === 'Enter') {
      if (this.isOpen && this.filteredOptions.length > 0) {
        event.preventDefault();
        const selected = this.filteredOptions[this.highlightedIndex];
        if (selected && !selected.disabled) {
          this.commitSelection(selected.value);
        }
      } else if (this.allowCustomValue) {
        event.preventDefault();
        this.commitSelection(this.searchQuery || this.value);
      }
    } else if (event.key === 'Escape') {
      this.isOpen = false;
      this.isTyping = false;
    } else if (event.key === 'Tab') {
      // Allow natural focus transition to forward or backward field
      if (this.allowCustomValue && this.isTyping) {
        this.commitSelection(this.searchQuery);
      }
      this.isOpen = false;
      this.isTyping = false;
    }
  }

  toggleOpen(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.isTyping = false;
      this.searchQuery = '';
      this.updateHighlightedIndex();
      this.inputRef?.nativeElement.focus();
    }
  }

  onSelectOption(option: SelectOption, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (option.disabled) return;
    this.commitSelection(option.value);
  }

  private commitSelection(val: any): void {
    this.value = val;
    this.isOpen = false;
    this.isTyping = false;
    this.searchQuery = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  private updateHighlightedIndex(): void {
    const idx = this.filteredOptions.findIndex((opt) => opt.value === this.value);
    this.highlightedIndex = idx >= 0 ? idx : 0;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.isTyping = false;
    }
  }

  writeValue(val: any): void {
    this.value = val;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
